import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate, fmtMoney, statusBadge } from "@/pages/admin/util";
import { Loader2, X, StickyNote, Save, Ban, Activity, Receipt, FileSearch } from "lucide-react";
import { toast } from "sonner";

export default function MemberDetailDialog({ memberId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/members/${memberId}`);
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId]);

  async function saveNote() {
    const note = newNote.trim();
    if (!note) return;
    setSaving(true);
    try {
      await adminApi.post(`/members/${memberId}/note`, { note });
      toast.success("Note saved");
      setNewNote("");
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  }

  async function cancel() {
    if (!window.confirm(`Cancel ${data.full_name}'s subscription?\nThey remain active until end of current period (no refund).`)) return;
    setSaving(true);
    try {
      const { data: res } = await adminApi.post(`/members/${memberId}/cancel`);
      toast.success(res.stripe?.applied ? "Cancelled in Stripe — ends at period end" : "Marked CANCELLED (no Stripe sub)");
      await load();
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancellation failed");
    } finally { setSaving(false); }
  }

  if (!memberId) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-[#071A33]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="member-detail-dialog">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2DCD0] sticky top-0 bg-white">
          <h2 className="font-display text-xl text-[#071A33]">Member detail</h2>
          <button onClick={onClose} className="p-1 text-[#475569] hover:text-[#071A33]" data-testid="member-detail-close"><X size={20}/></button>
        </div>

        {loading || !data ? (
          <div className="p-10 flex items-center gap-2 text-[#475569]"><Loader2 className="animate-spin" size={16}/> Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Profile */}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#B88A2A]">Member profile</p>
                <h3 className="font-display text-2xl text-[#071A33] mt-1">{data.full_name}</h3>
                <p className="text-sm text-[#475569]">{data.email} · {data.phone || "—"}</p>
                <p className="text-xs text-[#475569] mt-1">Joined {fmtDate(data.created_at, true)}</p>
              </div>
              <div className="text-right">{statusBadge(data.membership_status)}</div>
            </div>

            {/* Key facts */}
            <div className="bg-[#F7F4ED] p-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <F label="Park" value={data.park_name}/>
              <F label="Plot" value={data.plot_number}/>
              <F label="Town" value={data.town_location}/>
              <F label="Renewal date" value={fmtDate(data.current_period_end, true)}/>
              <F label="Cooling-off waived" value={data.cooling_off_waived ? "Yes" : "No"}/>
              <F label="Service used at" value={fmtDate(data.service_used_at, true)}/>
              <F label="Conversion source" value={data.conversion_source}/>
              <F label="Conversion case" value={data.conversion_case_id}/>
              <F label="Stripe customer" value={data.stripe_customer_id} mono/>
              <F label="Stripe subscription" value={data.stripe_subscription_id} mono/>
            </div>

            {/* Linked */}
            <div className="grid sm:grid-cols-2 gap-5">
              <SubList icon={<FileSearch size={14}/>} title="Linked cases" items={(data.cases || []).map(c => ({
                primary: c.case_type, secondary: `${c.issue_type || ""} ${c.status}`, date: c.created_at,
              }))}/>
              <SubList icon={<Receipt size={14}/>} title="Payments" items={(data.payments || []).map(p => ({
                primary: `${p.payment_type} · ${fmtMoney(p.amount)}`, secondary: `${p.status}`, date: p.paid_at || p.created_at,
              }))}/>
            </div>

            {/* Notes */}
            <div>
              <h4 className="font-display text-lg text-[#071A33] flex items-center gap-2"><StickyNote size={16} className="text-[#064E2E]"/> Notes</h4>
              <div className="mt-3 flex gap-2">
                <textarea rows={2} className="field-input flex-1" placeholder="Add internal note…" value={newNote} onChange={(e) => setNewNote(e.target.value)} data-testid="member-note-input"/>
                <button onClick={saveNote} disabled={saving || !newNote.trim()} className="btn-primary h-auto" data-testid="member-note-save">
                  {saving ? <Loader2 className="animate-spin" size={14}/> : <><Save size={14}/> Save</>}
                </button>
              </div>
              <ul className="mt-4 divide-y divide-[#E2DCD0]" data-testid="member-notes-list">
                {(data.notes || []).slice().reverse().map((n, i) => (
                  <li key={`note-${n.created_at || ""}-${i}`} className="py-2 text-sm">
                    <p className="text-[#071A33] whitespace-pre-wrap">{n.text}</p>
                    <p className="text-[11px] text-[#475569] mt-1">{fmtDate(n.created_at)}</p>
                  </li>
                ))}
                {(!data.notes || data.notes.length === 0) && <li className="text-sm text-[#475569] py-3">No notes yet.</li>}
              </ul>
            </div>

            {/* Activity log */}
            <div>
              <h4 className="font-display text-lg text-[#071A33] flex items-center gap-2"><Activity size={16} className="text-[#064E2E]"/> Activity log</h4>
              <ul className="mt-3 divide-y divide-[#E2DCD0]" data-testid="member-activity-log">
                {(data.activity_log || []).slice().reverse().map((a, i) => (
                  <li key={`act-${a.created_at || ""}-${a.action || ""}-${i}`} className="py-2 flex items-center justify-between text-sm">
                    <span className="font-mono text-[11px] text-[#064E2E]">{a.action}</span>
                    <span className="text-[11px] text-[#475569]">{fmtDate(a.created_at)}</span>
                  </li>
                ))}
                {(!data.activity_log || data.activity_log.length === 0) && <li className="text-sm text-[#475569] py-3">No activity yet.</li>}
              </ul>
            </div>

            {/* Actions */}
            <div className="border-t border-[#E2DCD0] pt-5 flex justify-between items-center flex-wrap gap-2">
              {data.membership_status !== "CANCELLED" ? (
                <button onClick={cancel} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#DC2626] text-white hover:bg-[#B91C1C] text-sm font-medium" data-testid="member-cancel-btn">
                  {saving ? <Loader2 className="animate-spin" size={14}/> : <Ban size={14}/>} Cancel subscription
                </button>
              ) : (
                <span className="text-sm text-[#475569]">This subscription is already cancelled.</span>
              )}
              <button onClick={onClose} className="btn-ghost">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function F({ label, value, mono }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#475569] font-medium">{label}</p>
      <p className={`text-[#071A33] ${mono ? "font-mono text-[11px]" : ""}`}>{String(value)}</p>
    </div>
  );
}

function SubList({ icon, title, items }) {
  return (
    <div>
      <h4 className="font-display text-sm text-[#071A33] flex items-center gap-2">{icon} {title}</h4>
      <ul className="mt-2 divide-y divide-[#E2DCD0] border border-[#E2DCD0] bg-white">
        {items.length === 0 && <li className="text-xs text-[#475569] px-3 py-3">None</li>}
        {items.slice(0, 6).map((it, i) => (
          <li key={`sub-${it.date || ""}-${it.primary || ""}-${i}`} className="px-3 py-2">
            <p className="text-xs font-medium text-[#071A33] truncate">{it.primary}</p>
            <p className="text-[11px] text-[#475569] truncate">{it.secondary}</p>
            <p className="text-[10px] text-[#475569] mt-0.5">{fmtDate(it.date, true)}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
